import { makeFormOptions, useAppForm } from "@/components/form";
import { isDefinedError } from "@/utils";
import { orpc } from "@/utils/clients/orpc";
import { CONSTANTS } from "@/utils/constants";
import { arrayBufferToBase64, encryptPrivateKey, generateKeyPair, storePrivateKey } from "@/utils/crypto";
import { useFormNavigationBlocker } from "@/utils/hooks/use-form-navigation-blocker";
import { Button, Card } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { AdminAgency } from "@workspace/admin-domain/src/features/agency/schema";
import { errorMessage } from "@workspace/domain/src/utils";
import { t } from "i18next";
import { LucideBarChart3, LucideBriefcase, LucideLock, LucideMapPin, LucideSave, LucideUsers2 } from "lucide-react";
import { Activity, useState } from "react";
import * as z from "zod";
import AgencyForm from "../_authenticated/-agency/-form/-index";

export const Route = createFileRoute("/onboarding/")({
  component: RouteComponent,
  beforeLoad: async ({ context }) => {
    const result = await context.queryClient.ensureQueryData(orpc.auth.getAuth.queryOptions());
    if (!result) throw redirect({ to: "/login" });
    if (result.user.agencyId) throw redirect({ to: "/" });
  },
});

const VaultPasswordSchema = z
  .object({
    password: z
      .string(errorMessage("Password is required", "Password is invalid"))
      .nonempty("Password is required")
      .max(255, "Password must be less than 255 characters"),
    confirmPassword: z
      .string(errorMessage("Password is required", "Password is invalid"))
      .nonempty("Password is required")
      .max(255, "Password must be less than 255 characters"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

function RouteComponent() {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [showVaultPassword, setShowVaultPassword] = useState(false);
  const [agencyFormData, setAgencyFormData] = useState<{
    payload: AdminAgency["Update"];
    file: File | undefined | null;
  } | null>(null);

  const createMutation = useMutation(
    orpc.agency.create.mutationOptions({
      onError: async (error) => {
        if (isDefinedError(error) && (error.code === "FORBIDDEN" || error.code === "UNAUTHORIZED")) {
          notifications.show({
            title: t("forbidden.title"),
            message: t("forbidden.description.action"),
            color: "red",
          });
          void navigate({ to: "/" });
          return;
        }

        notifications.show({
          title: error.message || t("unknownError"),
          message: t("agency.create.error.description"),
          color: "red",
        });
      },
    }),
  );

  return (
    <div className="px-md flex min-h-screen grow flex-col items-center justify-center bg-linear-to-b from-transparent to-blue-50/30 py-12 dark:to-blue-950/10">
      <div className="w-full max-w-7xl">
        <Activity mode={!showVaultPassword ? "visible" : "hidden"}>
          <div className="flex flex-col">
            <div className="mb-xl gap-md flex flex-col">
              <div className="flex size-16 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-950/50">
                <LucideBriefcase className="text-blue-filled size-8" />
              </div>
              <div className="gap-sm flex items-center">
                <h1 className="text-3xl font-bold md:text-4xl">{t("agency.title")}</h1>
              </div>
              <p className="text-dimmed text-lg">{t("agency.subtitle")}</p>
            </div>
            <div className="gap-xl grid lg:grid-cols-2 lg:gap-20">
              <AgencyForm
                mode="new"
                onSubmit={async ({ payload, file }) => {
                  setAgencyFormData({
                    payload,
                    file,
                  });
                  setShowVaultPassword(true);
                }}
              />
              <FeaturesSection />
            </div>
          </div>
        </Activity>
        <Activity mode={showVaultPassword ? "visible" : "hidden"}>
          <div className="gap-xl grid lg:grid-cols-[0.5fr_1fr_0.5fr] lg:gap-20">
            <div />
            <VaultPasswordForm
              onBack={() => {
                setShowVaultPassword(false);
                setAgencyFormData(null);
              }}
              onSubmit={async ({ password }) => {
                if (!agencyFormData) return;

                const keyPair = await generateKeyPair();
                const { encryptedKey, iv, salt } = await encryptPrivateKey(keyPair.privateKey, password);

                const publicKeyBuffer = await window.crypto.subtle.exportKey("spki", keyPair.publicKey);
                const publicKeyB64 = arrayBufferToBase64(publicKeyBuffer);

                await createMutation.mutateAsync({
                  ...agencyFormData.payload,
                  logo: agencyFormData.file ?? undefined,
                  publicKey: publicKeyB64,
                  encryptedPrivateKey: encryptedKey,
                  salt,
                  iv,
                });

                await storePrivateKey(keyPair.privateKey);

                notifications.show({
                  title: t("success"),
                  message: t("agency.create.success.description"),
                  color: "green",
                });

                await queryClient.invalidateQueries();
                await router.invalidate();
                window.location.href = "/";
              }}
            />
          </div>
        </Activity>
      </div>
    </div>
  );
}

function VaultPasswordForm({
  onBack,
  onSubmit,
}: {
  onBack: () => void;
  onSubmit: (data: { password: string; confirmPassword: string }) => Promise<void>;
}) {
  const form = useAppForm({
    ...makeFormOptions({
      defaultValues: {
        password: "",
        confirmPassword: "",
      },
      schema: VaultPasswordSchema,
    }),
    onSubmit: async ({ value }) => {
      await onSubmit({
        password: value.password,
        confirmPassword: value.confirmPassword,
      });
    },
  });

  useFormNavigationBlocker(form);

  return (
    <Card shadow="md" radius="lg" withBorder className="overflow-hidden">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
        noValidate
      >
        <Card.Section className="gap-lg p-lg md:p-xl flex flex-col">
          {/* Header Section */}
          <div className="gap-md flex flex-col">
            <div className="flex size-16 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-950/50">
              <LucideLock className="text-blue-filled size-8" />
            </div>
            <div className="flex flex-col gap-1.5">
              <h2 className="text-2xl font-bold">{t("onboarding.vaultPassword.title")}</h2>
              <p className="text-dimmed text-base">
                {t("onboarding.vaultPassword.description")}{" "}
                <span className="text-text">{t("onboarding.vaultPassword.descriptionHighlighted")}</span>
              </p>
            </div>
          </div>

          <div className="gap-lg flex flex-col">
            <form.AppField name="password">
              {(field) => (
                <field.PasswordField
                  label={t("onboarding.vaultPassword.password")}
                  leftSection={<LucideLock className="size-4" />}
                />
              )}
            </form.AppField>
            <form.AppField name="confirmPassword">
              {(field) => (
                <field.PasswordField
                  label={t("onboarding.vaultPassword.confirmPassword")}
                  leftSection={<LucideLock className="size-4" />}
                />
              )}
            </form.AppField>
          </div>
        </Card.Section>

        {/* Submit Section */}
        <Card.Section className="p-lg md:p-xl border-t bg-gray-50/50 dark:border-gray-800 dark:bg-gray-900/20">
          <div className="gap-md flex flex-col">
            <form.AppForm>
              <div className="gap-sm flex">
                <Button
                  size="lg"
                  variant="default"
                  onClick={onBack}
                  className="flex-1"
                  disabled={form.state.isSubmitting}
                >
                  {t("back")}
                </Button>
                <form.SubmitButton
                  size="lg"
                  className="flex-1 font-semibold"
                  leftSection={<LucideSave className="size-5" />}
                  disabled={CONSTANTS.DEMO}
                >
                  {t("continue")}
                </form.SubmitButton>
              </div>
            </form.AppForm>
          </div>
        </Card.Section>
      </form>
    </Card>
  );
}

function FeaturesSection() {
  return (
    <div className="flex flex-col">
      <div className="lg:gap-xl hidden grow lg:flex lg:flex-col lg:justify-center">
        <div className="gap-md flex flex-col">
          <h2 className="text-2xl font-bold">{t("agency.benefits.title")}</h2>
          <p className="text-dimmed text-lg">Everything you need to manage your travel agency in one place</p>
        </div>

        <div className="gap-lg flex flex-col">
          {/* Benefit 1 */}
          <div className="gap-md flex">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-950/50">
              <LucideMapPin className="text-blue-filled size-6" />
            </div>
            <div className="flex flex-col gap-1.5">
              <h3 className="text-lg font-semibold">{t("agency.benefits.trips.title")}</h3>
              <p className="text-dimmed">{t("agency.benefits.trips.description")}</p>
            </div>
          </div>

          {/* Benefit 2 */}
          <div className="gap-md flex">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-green-100 dark:bg-green-950/50">
              <LucideUsers2 className="text-green-filled size-6" />
            </div>
            <div className="flex flex-col gap-1.5">
              <h3 className="text-lg font-semibold">{t("agency.benefits.clients.title")}</h3>
              <p className="text-dimmed">{t("agency.benefits.clients.description")}</p>
            </div>
          </div>

          {/* Benefit 3 */}
          <div className="gap-md flex">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-purple-100 dark:bg-purple-950/50">
              <LucideBarChart3 className="text-purple-filled size-6" />
            </div>
            <div className="flex flex-col gap-1.5">
              <h3 className="text-lg font-semibold">{t("agency.benefits.analytics.title")}</h3>
              <p className="text-dimmed">{t("agency.benefits.analytics.description")}</p>
            </div>
          </div>

          {/* Benefit 4 */}
          <div className="gap-md flex">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-orange-100 dark:bg-orange-950/50">
              <LucideBriefcase className="text-orange-filled size-6" />
            </div>
            <div className="flex flex-col gap-1.5">
              <h3 className="text-lg font-semibold">{t("agency.benefits.team.title")}</h3>
              <p className="text-dimmed">{t("agency.benefits.team.description")}</p>
            </div>
          </div>
        </div>

        {/* Decorative element */}
        <div className="mt-md p-lg rounded-lg border-2 border-dashed border-blue-200 dark:border-blue-900">
          <p className="text-dimmed text-base italic">{t("agency.benefits.subtitle")}</p>
        </div>
      </div>
    </div>
  );
}
